import { useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { authApi, type RegisterAccountCheck } from '@/api/auth.api';
import type { ModuleEntry } from '@/config/moduleEntries';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import { getOrCreateDeviceId } from '@/core/auth/deviceId';
import { AuthButton } from '@/core/auth/components/AuthButton';
import { AuthCard } from '@/core/auth/components/AuthCard';
import { AuthInput } from '@/core/auth/components/AuthInput';
import { Button } from '@/components/ui/Button';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import {
  clearPlatformOnboardingRequired,
  requireFattahOneOnboarding,
  resolveModuleIntent,
  writeOnboardingState,
  type OnboardingModule,
} from '@/modules/onboarding/onboardingState';
import type { ErpModule } from '@/api/platform.api';
import { platformApi } from '@/api/platform.api';
import { enabledHeadModuleCodes, enabledHeadModuleEntries } from '@/config/moduleEntries';
import { BRAND } from '@/config/brand';
import { hydrateFromLogin, setAccessibleModules, setEnabledModules, setWorkspaceModule } from '@/store/authSlice';

const passwordSchema = z
  .string()
  .min(8, 'At least 8 characters')
  .regex(/[A-Z]/, 'Include an uppercase letter')
  .regex(/[a-z]/, 'Include a lowercase letter')
  .regex(/[0-9]/, 'Include a number')
  .regex(/[^A-Za-z0-9]/, 'Include a special character');

const fullSchema = z
  .object({
    name: z.string().min(2, 'Name required'),
    email: z.string().email('Valid email required'),
    phone: z.string().min(8, 'Phone number required'),
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Passwords must match',
    path: ['confirmPassword'],
  });

type FormData = z.infer<typeof fullSchema>;

type FlowStep = 'form' | 'existing';

function onboardingIntentFromErpModule(erpModule: ErpModule): OnboardingModule {
  const map: Partial<Record<ErpModule, OnboardingModule>> = {
    POS: 'POS',
    CRM: 'CRM',
    HR: 'HR',
    FINANCE: 'FINANCE',
    PROJECTS: 'PROJECTS',
    DESK: 'DESK',
    SOCIAL: 'SOCIAL',
  };
  return map[erpModule] ?? 'POS';
}

const ONBOARDING_HEAD_MODULES: { code: OnboardingModule; erpModule: ErpModule }[] = [
  { code: 'POS', erpModule: 'POS' },
  { code: 'CRM', erpModule: 'CRM' },
  { code: 'HR', erpModule: 'HR' },
  { code: 'FINANCE', erpModule: 'FINANCE' },
  { code: 'PROJECTS', erpModule: 'PROJECTS' },
  { code: 'DESK', erpModule: 'DESK' },
  { code: 'SOCIAL', erpModule: 'SOCIAL' },
];

async function enableOnlyModuleForNewOrg(
  intent: OnboardingModule,
  dispatch: ReturnType<typeof useAppDispatch>,
) {
  await Promise.all(
    ONBOARDING_HEAD_MODULES.map(({ erpModule, code }) =>
      platformApi.updateModule(erpModule, code === intent),
    ),
  );
  const headCodes = enabledHeadModuleCodes([intent]);
  dispatch(setEnabledModules(headCodes));
  dispatch(setAccessibleModules(headCodes));
}

function moduleSlugForCheck(moduleEntry?: ModuleEntry, intentModule?: OnboardingModule | null): string | undefined {
  if (moduleEntry?.slug) return moduleEntry.slug;
  if (intentModule) return intentModule.toLowerCase();
  return undefined;
}

export interface RegisterWizardProps {
  moduleEntry?: ModuleEntry;
}

export function RegisterWizard({ moduleEntry }: RegisterWizardProps) {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const intentModule = useMemo(() => {
    if (moduleEntry) {
      return onboardingIntentFromErpModule(moduleEntry.erpModule);
    }
    return resolveModuleIntent(location.search);
  }, [moduleEntry, location.search]);

  const signInPath = moduleEntry?.loginPath ?? MODULE_HUB_PATH;
  const forgotPasswordPath = '/forgot-password';

  const [flowStep, setFlowStep] = useState<FlowStep>('form');
  const [existingAccount, setExistingAccount] = useState<RegisterAccountCheck | null>(null);
  const [formSnapshot, setFormSnapshot] = useState<FormData | null>(null);
  const [addingApp, setAddingApp] = useState(false);
  const [existingNotice, setExistingNotice] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(fullSchema), mode: 'onBlur' });

  const resetToForm = () => {
    setFlowStep('form');
    setExistingAccount(null);
    setFormSnapshot(null);
    setExistingNotice(null);
    clearErrors();
  };

  const finishLogin = (
    result: Awaited<ReturnType<typeof authApi.login>>,
    options?: { newOrganization?: boolean; skipPlatformOnboarding?: boolean },
  ) => {
    dispatch(hydrateFromLogin(result));
    if (options?.skipPlatformOnboarding) {
      clearPlatformOnboardingRequired();
    } else if (options?.newOrganization) {
      requireFattahOneOnboarding();
    } else {
      clearPlatformOnboardingRequired();
    }
  };

  const createNewAccount = async (data: FormData) => {
    const [firstName, ...rest] = data.name.trim().split(/\s+/);
    const lastName = rest.join(' ') || undefined;
    const result = await authApi.register({
      organization: {
        name: `${data.name.trim()}'s Organization`,
        email: data.email,
        phone: data.phone,
        timezone: 'Asia/Kolkata',
      },
      ownerEmail: data.email,
      ownerPassword: data.password,
      ownerFirstName: firstName,
      ownerLastName: lastName,
      deviceId: getOrCreateDeviceId(),
    });

    if (moduleEntry && intentModule) {
      finishLogin(result, { newOrganization: true, skipPlatformOnboarding: true });
      await enableOnlyModuleForNewOrg(intentModule, dispatch);
      dispatch(setWorkspaceModule(moduleEntry.erpModule));
      writeOnboardingState({
        step: 0,
        intentModule,
        selectedModules: [intentModule],
        selectedPlan: 'FREE',
      });
      notify.success(`Welcome to ${moduleEntry.label}`);
      navigate(moduleEntry.homePath, { replace: true });
      return;
    }

    finishLogin(result, { newOrganization: true });
    writeOnboardingState({
      step: 0,
      intentModule,
      selectedModules: intentModule ? [intentModule] : ['POS'],
      selectedPlan: 'FREE',
    });
    notify.success(`Welcome to ${BRAND.name}`);
    navigate('/onboarding/products', { replace: true });
  };

  const signInExisting = async (data: FormData) => {
    const result = await authApi.login({
      email: data.email.trim(),
      password: data.password,
      deviceId: getOrCreateDeviceId(),
      entryModule: moduleEntry?.slug,
    });
    finishLogin(result);
    notify.success('Signed in to your existing account');
    if (moduleEntry) {
      navigate(moduleEntry.homePath);
    } else {
      navigate(MODULE_HUB_PATH);
    }
  };

  const onSubmit = async (data: FormData) => {
    setExistingNotice(null);
    clearErrors();

    try {
      const check = await authApi.checkRegisterAccount({
        email: data.email.trim(),
        password: data.password,
        module: moduleSlugForCheck(moduleEntry, intentModule),
      });

      if (check.exists && !check.passwordValid) {
        setError('password', {
          type: 'manual',
          message: 'Password does not match this email',
        });
        setExistingNotice(
          'This email is already registered, but the password is incorrect. Sign in with the right password, reset it, or use a different email.',
        );
        return;
      }

      if (check.exists && check.passwordValid) {
        setFormSnapshot(data);
        setExistingAccount(check);
        setFlowStep('existing');
        return;
      }

      await createNewAccount(data);
    } catch (err) {
      showApiError(err, 'Could not complete registration');
    }
  };

  const addAppToOrg = async () => {
    const data = formSnapshot ?? watch();
    if (!existingAccount || !moduleEntry) return;
    setAddingApp(true);
    try {
      const result = await authApi.addApplication({
        email: data.email.trim(),
        password: data.password,
        module: moduleEntry.slug,
        deviceId: getOrCreateDeviceId(),
      });
      finishLogin(result);
      notify.success(`${moduleEntry.label} added to ${existingAccount.organizationName ?? 'your organization'}`);
      navigate(moduleEntry.homePath);
    } catch (err) {
      showApiError(err, 'Could not add application to your organization');
    } finally {
      setAddingApp(false);
    }
  };

  const continueWithExisting = async () => {
    const data = formSnapshot;
    if (!data) return;
    setAddingApp(true);
    try {
      await signInExisting(data);
    } catch (err) {
      showApiError(err, 'Could not sign in');
    } finally {
      setAddingApp(false);
    }
  };

  const title = moduleEntry ? '' : BRAND.name;
  const subtitle = 'Create your account — choose your products on the next screen';

  if (flowStep === 'existing' && existingAccount?.passwordValid && formSnapshot) {
    const appLabel = moduleEntry?.label;
    const orgName = existingAccount.organizationName ?? 'your organization';
    const enabledHeadApps = enabledHeadModuleEntries(existingAccount.enabledModules ?? []);

    return (
      <AuthCard
        title={appLabel ? '' : BRAND.name}
        subtitle={
          appLabel
            ? `Your password matches — you can add ${appLabel} to your existing organization`
            : 'Your password matches — sign in to continue with your existing organization'
        }
      >
        <div className="space-y-4">
          <div className="flex gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100">
            <CheckCircleOutlineIcon className="mt-0.5 shrink-0" fontSize="small" />
            <div>
              <p className="font-medium">Account verified</p>
              <p className="mt-0.5 text-emerald-800/90 dark:text-emerald-200/90">
                Signed in as <span className="font-medium">{formSnapshot.email}</span>
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Organization</p>
            <p className="mt-1 text-base font-semibold text-slate-900 dark:text-slate-100">{orgName}</p>
            {enabledHeadApps.length > 0 && (
              <>
                <p className="mt-3 text-xs font-medium uppercase tracking-wide text-slate-500">Your applications</p>
                <ul className="mt-2 flex flex-wrap gap-2">
                  {enabledHeadApps.map((entry) => (
                    <li
                      key={entry.erpModule}
                      className="rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-600"
                    >
                      {entry.label}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          {appLabel && existingAccount.moduleAlreadyEnabled ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
              <p>
                <strong>{appLabel}</strong> is already on this organization. Sign in to open it — no need to register
                again.
              </p>
            </div>
          ) : appLabel ? (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Add <strong>{appLabel}</strong> to <strong>{orgName}</strong>? Your team keeps one login and one
              organization — billing and users stay unified.
            </p>
          ) : (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              You already have an account with <strong>{orgName}</strong>. Continue to sign in — we will not create a
              duplicate organization.
            </p>
          )}

          <div className="flex flex-col gap-2 pt-1">
            {appLabel && !existingAccount.moduleAlreadyEnabled && (
              <AuthButton type="button" loading={addingApp} onClick={() => void addAppToOrg()}>
                Add {appLabel} to {orgName}
              </AuthButton>
            )}
            {(existingAccount.moduleAlreadyEnabled || !appLabel) && (
              <AuthButton type="button" loading={addingApp} onClick={() => void continueWithExisting()}>
                Sign in to {orgName}
              </AuthButton>
            )}
            {appLabel && !existingAccount.moduleAlreadyEnabled && (
              <Button type="button" variant="secondary" className="w-full" onClick={() => void continueWithExisting()}>
                Sign in without adding {appLabel}
              </Button>
            )}
            <Link to={signInPath} className="text-center text-sm text-primary-600 hover:underline dark:text-primary-400">
              Go to sign-in page
            </Link>
            <button type="button" className="text-xs text-slate-500 hover:underline" onClick={resetToForm}>
              Use a different email
            </button>
          </div>
        </div>
      </AuthCard>
    );
  }

  return (
    <>
      <AuthCard title={title} subtitle={subtitle}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {existingNotice && (
            <div
              className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100"
              role="alert"
            >
              <ErrorOutlineIcon className="mt-0.5 shrink-0" fontSize="small" />
              <div>
                <p className="font-medium">Already registered</p>
                <p className="mt-0.5">{existingNotice}</p>
                <p className="mt-2">
                  <Link to={signInPath} className="font-medium underline">
                    Sign in
                  </Link>
                  {' · '}
                  <Link to={forgotPasswordPath} className="font-medium underline">
                    Reset password
                  </Link>
                </p>
              </div>
            </div>
          )}

          <AuthInput label="Name" autoComplete="name" {...register('name')} error={errors.name?.message} />
          <AuthInput
            label="Email"
            type="email"
            autoComplete="email"
            {...register('email')}
            error={errors.email?.message}
          />
          <AuthInput
            label="Phone number"
            type="tel"
            autoComplete="tel"
            {...register('phone')}
            error={errors.phone?.message}
          />
          <AuthInput
            label="Password"
            type="password"
            autoComplete="new-password"
            {...register('password')}
            error={errors.password?.message}
          />
          <AuthInput
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            {...register('confirmPassword')}
            error={errors.confirmPassword?.message}
          />

          <AuthButton type="submit" loading={isSubmitting}>
            Continue
          </AuthButton>
        </form>

        <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
          Already registered?{' '}
          <Link to={signInPath} className="font-medium text-primary-600 hover:underline dark:text-primary-400">
            Sign in
          </Link>
          {!moduleEntry && (
            <>
              {' · '}
              <Link to="/" className="font-medium text-primary-600 hover:underline dark:text-primary-400">
                Fattah sign in
              </Link>
            </>
          )}
        </p>
      </AuthCard>
    </>
  );
}
